import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'components/Breadcrumbs';
import { searchChemicals } from 'utils/methods/safetynow';
import {animateScroll as scroll} from 'react-scroll'

const SearchResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('query') || '');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastOnChangeTime, setLastOnChangeTime] = useState(null);

  useEffect(() => {
    const query = searchParams.get('query');
    if (query) {
      setSearchQuery(query);
      performSearch(query);
      scroll.scrollToTop();
    }

    
  }, [searchParams]);
  
  useEffect(() => {
    if (!searchQuery) setSearchResults([]);
  }, [searchQuery]);

  const performSearch = async (query = searchQuery) => {
    if (!query.trim()) return;
    
    setLoading(true);
    setErrorMessage('');
    setSearchResults([]);
    setCurrentPage(1);

    try {
      const response = await searchChemicals(query, 1);
      if (response.data.status === 'error') {
        setErrorMessage(response.data.message);
      } else {
        setSearchResults(response.data.results || []);
      }
    } catch (error) {
      console.error('Error during search:', error);
      setErrorMessage(error.message || 'Failed to fetch search results');
    } finally {
      setLoading(false);
    }
  };

  const handleOnChange = (e) => {
    const currentTime = new Date().getTime();
    setSearchQuery(prev => e.target.value)
    setSearchResults(prev => [])
    if (lastOnChangeTime && currentTime - lastOnChangeTime < 300) {
      return; // Throttle to once every 300ms
    }
    setLastOnChangeTime(currentTime);
    if (e.target.value.trim()) performSearch(e.target.value)
  }

  const formatProductName = (name) => {
    return name
      // .split('-')
      // .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      // .join(' ')
  };

  const navigateToProfile = (chemical) => {
    // Use the chemical's id as cid and store relevant data
    const productData = {
      name: chemical.name,
      cid: chemical.id,
      cas_number: chemical.cas_number,
      formula: chemical.formula,
      molecular_weight: chemical.molecular_weight,
      synonyms: chemical.synonyms || []
    };
    
    // Store data in localStorage
    localStorage.setItem('selectedProduct', JSON.stringify(productData));
    
    navigate(`/tools/safety-now/search/${encodeURIComponent(chemical.name)}/${chemical.id}`);
  };

  const loadNextPage = async () => {
    if (loading) return;
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      const response = await searchChemicals(searchQuery, currentPage + 1);
      if (response.data.status === 'error') {
        setErrorMessage(response.data.message);
      } else if (response.data.results && response.data.results.length > 0) {
        setSearchResults(prev => [...prev, ...response.data.results]);
        setCurrentPage(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error during search:', error);
      setErrorMessage(error.message || 'Failed to fetch more results');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-center text-gray-800 mb-8">
            Search Chemicals
          </h1>
          <Breadcrumbs />
          
          {/* Search Bar */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex gap-4 flex-col md:flex-row">
              <input 
                value={searchQuery}
                onChange={handleOnChange}
                onKeyPress={(e) => e.key === 'Enter' && performSearch(searchQuery)}
                placeholder="Enter chemical name or CAS number..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all w-full"
              />
              <button 
                onClick={() => performSearch(searchQuery)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Search
              </button>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-6">Search Results</h2>
          
          {/* Loading Spinner */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}
          
          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {errorMessage}
            </div>
          )}
          
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-4">
              {searchResults.map((result, index) => (
                <div key={`${result.name}-${index}`} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
                  <div className="result-content">
                    <div className="result-header mb-4">
                      <h3 
                        className="text-xl font-bold text-gray-800 cursor-pointer hover:text-blue-600 transition-colors"
                        onClick={() => navigateToProfile(result)}
                      >
                        {formatProductName(result.name)}
                      </h3>
                    </div>
                    <div className="result-body">
                      {result.description && (
                        <p className="text-gray-600 mb-4">{result.description}</p>
                      )}
                      
                      {/* Additional Info */}
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        {result.id && (
                          <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full">
                            ID: {result.id}
                          </span>
                        )}
                        {result.cas_number && (
                          <span className="bg-purple-50 text-purple-600 px-3 py-1 rounded-full">
                            CAS: {result.cas_number}
                          </span>
                        )}
                        {result.formula && (
                          <span className="bg-green-50 text-green-600 px-3 py-1 rounded-full">
                            Formula: {result.formula}
                          </span>
                        )}
                        {result.molecular_weight && (
                          <span className="bg-orange-50 text-orange-600 px-3 py-1 rounded-full">
                            MW: {result.molecular_weight} {result.molecular_weight_unit || 'g/mol'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Load More Button */}
              <div className="text-center mt-8">
                <button 
                  onClick={loadNextPage}
                  disabled={loading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {loading ? 'Loading...' : 'Load More Results'}
                </button>
              </div>
            </div>
          )}
          
          {/* No Results */}
          {!loading && !errorMessage && searchResults.length === 0 && searchQuery && (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">
                No results found for "{searchQuery}".
              </div>
              <p className="text-gray-400 mt-2">
                Try different keywords or check the spelling.
              </p>
            </div>
          )}
          {!searchQuery && (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">
                Please enter a chemical name or CAS number to search.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResult;
